'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Share2, Check, Loader2, Image as ImageIcon, Link as LinkIcon } from 'lucide-react';
import { toPng, toBlob } from 'html-to-image';
import { TwitterProfileAnalysis } from '@/types/twitter-analysis';
import { XIcon } from '@/components/icons/SocialIcons';
import { trackShare } from '@/lib/analytics';
import ReconIntelShareCard from './ReconIntelShareCard';

interface ReconIntelShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  analysis: TwitterProfileAnalysis;
  entityName?: string;
}

export default function ReconIntelShareModal({
  isOpen,
  onClose,
  analysis,
  entityName,
}: ReconIntelShareModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate image on modal open
  useEffect(() => {
    if (isOpen && cardRef.current && !imageUrl) {
      generateImage();
    }
  }, [isOpen]);

  const generateImage = useCallback(async () => {
    if (!cardRef.current) return;

    setIsGenerating(true);
    setError(null);

    try {
      // Wait for any images to load
      await new Promise(resolve => setTimeout(resolve, 100));

      const dataUrl = await toPng(cardRef.current, {
        quality: 1,
        pixelRatio: 2, // Retina quality
        backgroundColor: '#0a0a0a',
      });

      setImageUrl(dataUrl);
    } catch (err) {
      console.error('Failed to generate image:', err);
      setError('Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const handleDownload = useCallback(async () => {
    if (!cardRef.current) return;

    setIsGenerating(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#0a0a0a',
      });

      // Create download link
      const link = document.createElement('a');
      link.download = `scouts-take-${entityName || analysis.handleId}-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();

      trackShare({
        platform: 'other',
        contentType: 'coin',
        contentTitle: `RECON Intel Download - ${entityName || analysis.handleId}`,
        contentUrl: window.location.href,
      });
    } catch (err) {
      console.error('Failed to download:', err);
      setError('Failed to download image.');
    } finally {
      setIsGenerating(false);
    }
  }, [analysis.handleId, entityName]);

  const handleShareToX = useCallback(async () => {
    // Create share text - sentiment is about what the NEWS means for ecosystem
    const handle = entityName ? `@${entityName}` : analysis.handleId;
    
    // Map sentiment to readable format for share (simpler, cleaner)
    const sentimentEmoji: Record<string, string> = {
      bullish: '🟢',
      bearish: '🔴',
      neutral: '⚪',
      informational: 'ℹ️'
    };
    const emoji = sentimentEmoji[analysis.sentiment] || '⚪';
    
    // Truncate summary for tweet
    const maxSummaryLength = 180;
    const truncatedSummary = analysis.summary.length > maxSummaryLength 
      ? analysis.summary.substring(0, maxSummaryLength) + '...'
      : analysis.summary;

    const tweetText = `${emoji} ${handle}\n\n"${truncatedSummary}"\n\n— RECON Intel`;
    
    // Note: For direct image upload, users need to download first
    // X's Web Intent doesn't support image upload
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&via=DescoutHQ`;
    
    window.open(twitterUrl, '_blank', 'width=550,height=520');

    trackShare({
      platform: 'twitter',
      contentType: 'coin',
      contentTitle: `RECON Intel - ${entityName || analysis.handleId}`,
      contentUrl: window.location.href,
    });
  }, [analysis, entityName]);

  const handleCopyImage = useCallback(async () => {
    if (!cardRef.current) return;

    try {
      const blob = await toBlob(cardRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#0a0a0a',
      });

      if (blob) {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);

        trackShare({
          platform: 'copy_link',
          contentType: 'coin',
          contentTitle: `RECON Intel - ${entityName || analysis.handleId}`,
          contentUrl: window.location.href,
        });
      }
    } catch (err) {
      console.error('Failed to copy image:', err);
      // Fallback: Copy summary text
      try {
        await navigator.clipboard.writeText(analysis.summary);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        setError('Failed to copy. Please try downloading instead.');
      }
    }
  }, [analysis, entityName]);

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!isOpen || !mounted) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/95"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-neutral-900 rounded-2xl border border-neutral-800 shadow-2xl w-[calc(100%-16px)] max-w-[600px] max-h-[calc(100vh-32px)] overflow-y-auto mx-2">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral-800">
          <div className="flex items-center gap-2 sm:gap-3">
            <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-sky-400" />
            <h2 className="text-base sm:text-lg font-semibold text-white">Share RECON Intel</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 sm:p-2 hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Preview Card */}
          <div className="flex justify-center mb-4 sm:mb-6">
            <div className="relative w-full max-w-[560px]">
              {/* Loading overlay */}
              {isGenerating && (
                <div className="absolute inset-0 bg-neutral-900/80 rounded-2xl flex items-center justify-center z-10">
                  <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-sky-400 animate-spin" />
                </div>
              )}
              
              {/* The actual card to capture */}
              <ReconIntelShareCard
                ref={cardRef}
                analysis={analysis}
                entityName={entityName}
              />
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-950/50 border border-red-900/50 rounded-lg text-red-400 text-sm text-center">
              {error}
            </div>
          )}

          {/* Share tips */}
          <div className="bg-neutral-800/50 rounded-xl p-3 sm:p-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <ImageIcon className="w-4 h-4 sm:w-5 sm:h-5 text-sky-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm text-neutral-300 leading-relaxed">
                <strong className="text-white">Hint:</strong> Download the image, then attach it to your X post for maximum engagement!
              </p>
            </div>
          </div>
        </div>

        {/* Actions Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-neutral-800 bg-neutral-900/80">
          <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
            {/* Download Button */}
            <button
              onClick={handleDownload}
              disabled={isGenerating}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white text-sm sm:text-base font-medium rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-sky-500/20"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Download</span> Image
            </button>

            {/* Share on X Button */}
            <button
              onClick={handleShareToX}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-black hover:bg-neutral-900 text-white text-sm sm:text-base font-medium rounded-xl border border-neutral-700 transition-all"
            >
              <XIcon className="w-4 h-4" />
              Share on X
            </button>

            {/* Copy Image Button */}
            <button
              onClick={handleCopyImage}
              disabled={isGenerating}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-medium transition-all border ${
                copied
                  ? 'bg-green-600 border-green-500 text-white'
                  : 'bg-neutral-800 border-neutral-700 text-neutral-300 hover:bg-neutral-700 hover:text-white'
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <LinkIcon className="w-4 h-4" />
                  Copy
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

